/**
 * Human-readable label helpers for the Activity log (DNKADM-0029 U1).
 *
 * The `/v2/admin/audit` feed carries both human-ready fields (`detail`, `note`) and
 * machine keys (`action`, credit `reason`, raw `bucket`). These helpers turn an
 * `ActivityEntry` into the strings the row renders, so the global Activity log
 * (`ActivityLog.tsx`) and the member Activity tab (`MemberActivityTab.tsx`) lead
 * with the human fact and NEVER surface the machine `action` or a raw id.
 *
 * Pure, render-safe, total: every helper degrades to a sensible fallback (never
 * throws) so a missing/odd field never breaks a row — mirroring the parser's
 * "a missing field degrades, never throws" posture (`api/audit.ts`).
 */
import { ACTIVITY_ACTION_LABELS, type ActivityEntry, type SessionMode } from '@/api/audit';
import { humanizeToken } from '@/features/transaction-ledger/transactionFormat';
import { formatMinorMoney } from '@/utils/v2Format';
import { scrubBrand } from '@/utils/whitelabel';
import {
  declineReasonFromMessage,
  declineReasonLine,
  isProviderMessage,
  knownVaultSentenceLine,
} from '@/utils/declineReason';

/**
 * Map a machine `action` key → a human title. The real `kind` set + labels are the
 * shared `ACTIVITY_ACTION_LABELS` (DNKADM-0151); the legacy v1 `booking_*` keys are
 * kept for back-compat with any v1-shaped row. The retired `purchase_*` (money →
 * Transaction Ledger) keys are gone. Used only when the entry has no human `detail`
 * to lead with.
 */
const ACTION_TITLES: Record<string, string> = {
  ...ACTIVITY_ACTION_LABELS,
  // Legacy v1 reservation keys (back-compat) → the same reservation labels.
  booking_create: ACTIVITY_ACTION_LABELS.reservation_create,
  booking_cancel: ACTIVITY_ACTION_LABELS.reservation_cancel,
  booking_reschedule: ACTIVITY_ACTION_LABELS.reservation_reschedule,
};

/**
 * The Type-filter option label for a `kind` from `GET /v2/admin/audit/kinds`
 * (DNKADM-0259): our copy from the shared {@link ACTIVITY_ACTION_LABELS} when we carry
 * it, otherwise the value HUMANIZED (`walk_in_credit_sale` → "Walk in credit sale") via
 * the existing ledger humanizer.
 *
 * Labels stay client-side on purpose (the white-label scrub seam and per-repo wording),
 * so the route is free to add actions ahead of our copy: a missing label is cosmetic —
 * the value is still offered and still filterable, never dropped and never rendered as
 * a raw snake_case token.
 */
export function activityKindLabel(value: string): string {
  // The FILTER label map is `ACTIVITY_ACTION_LABELS` only — not `ACTION_TITLES`, whose
  // legacy v1 `booking_*` keys are row back-compat and are never filter values.
  const labels: Record<string, string> = ACTIVITY_ACTION_LABELS;
  return labels[value] ?? humanizeToken(value);
}

/**
 * Map a credit `reason` key → a human title (credit rows have no `action` worth
 * showing). Covers the ledger reasons the feed emits.
 */
const CREDIT_REASON_TITLES: Record<string, string> = {
  import: 'Imported balance',
  monthly_refill: 'Monthly refill',
  booking_debit: 'Booking',
  cancel_refund: 'Cancellation refund',
  admin_adjust: 'Manual adjustment',
  package_grant: 'Package credit',
  standalone_purchase: 'Purchase',
  expiry: 'Expired',
};

/** Title-case a single word (e.g. `bay` → `Bay`). Empty stays empty. */
function titleCaseWord(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}

/**
 * Humanize a raw GHL bucket key: strip a trailing `_credits`, replace `_` with
 * spaces, title-case → `bay_reservation_credits` → "Bay reservation",
 * `lesson_credits` → "Lesson". Returns `null` for an absent/blank bucket.
 *
 * Only the FIRST word is capitalized (sentence-style), matching the spec's
 * examples ("Bay reservation", not "Bay Reservation").
 */
export function humanizeBucket(bucket: string | null): string | null {
  if (bucket === null) return null;
  const stripped = bucket.replace(/_credits$/i, '');
  const words = stripped.split('_').filter((w) => w.length > 0);
  if (words.length === 0) return null;
  return [titleCaseWord(words[0]!), ...words.slice(1)].join(' ');
}

/**
 * The row title — the human fact, NEVER the machine `action`. Prefers the
 * already-human `detail`; when absent, maps from the keys: for credit rows the
 * credit `reason`, otherwise the `action`. Falls back to a neutral "Activity"
 * when nothing maps, so a row always has a title.
 */
export function activityTitle(entry: ActivityEntry): string {
  // `detail` is backend human copy rendered as-is — brand-scrub it at this
  // render boundary (DNKADM-0044) so a stray "GoHighLevel"/"GHL" surfaces as
  // "Journix". A no-op on clean copy. The key→title maps below are clean local
  // constants, so they need no scrub.
  if (entry.detail !== null) return scrubBrand(entry.detail)!;

  const refused = refusedActionTitle(entry);
  if (refused !== null) return refused;

  if (entry.kind === 'credit' && entry.reason !== null) {
    const mapped = CREDIT_REASON_TITLES[entry.reason.toLowerCase()];
    if (mapped !== undefined) return mapped;
  }

  if (entry.action !== null) {
    const action = entry.action.toLowerCase();
    const mapped = ACTION_TITLES[action];
    // An order row carries its order number in `reason` (the audit writer stores the
    // entity ref there): "Order comped (ORD-1042)" tells the desk WHICH order (DNKADM-0448).
    if (mapped !== undefined) {
      return ORDER_ACTION.test(action) ? withReference(mapped, entry.reason) : mapped;
    }
    // A type we carry no label for reads as its real type, humanized, with whatever
    // reference the row has, never the bare word "Activity" (DNKADM-0448).
    return withReference(humanizeToken(entry.action), isOpaqueRef(entry.reason) ? null : entry.reason);
  }

  return 'Activity';
}

/** The order-flavoured audit actions whose `reason` is the order number. */
const ORDER_ACTION = /^order_/;

/** A UUID or long hex-ish id: never shown on a row (the audit `reason` is often the entity ref). */
function isOpaqueRef(ref: string | null): boolean {
  return ref !== null && /^[0-9a-f-]{16,}$/i.test(ref.trim());
}

/** `Title (reference)`, or just `Title` when the row has no reference. */
function withReference(title: string, reference: string | null): string {
  const ref = reference?.trim() ?? '';
  return ref.length === 0 ? title : `${title} (${scrubBrand(ref)})`;
}

/** `details` string field, or `null` when absent/blank/not a string. */
function detailsText(entry: ActivityEntry, key: string): string | null {
  const v = entry.details?.[key];
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

/** The refused-action row's `action` key (DNKAPI-0613). */
const REFUSED_ACTION = 'reservation_action_refused';

/**
 * Attempted actions whose humanized token reads badly as a title. `create` is a refused
 * NEW booking (DNKAPI-0687: a staff create-on-behalf or the member's own booking), which
 * has no reservation yet, so it reads "Booking refused", not "Create refused".
 */
const ATTEMPTED_ACTION_TITLES: Record<string, string> = {
  create: 'Booking',
};

/**
 * The What title of a refused action (DNKADM-0428): "Cancel refused", "Check in
 * refused", "Booking refused", … from `details.attemptedAction` (humanized — a new
 * attempted action still reads sensibly). Since DNKAPI-0687 the row can be the member's
 * own (actorType MEMBER) as well as the desk's. `null` for any other row; a refused row
 * with no decodable `attemptedAction` reads the generic "Action refused".
 */
function refusedActionTitle(entry: ActivityEntry): string | null {
  if (entry.action?.toLowerCase() !== REFUSED_ACTION) return null;
  const attempted = detailsText(entry, 'attemptedAction');
  if (attempted === null) return 'Action refused';
  const key = attempted.trim().toLowerCase();
  const label = Object.prototype.hasOwnProperty.call(ATTEMPTED_ACTION_TITLES, key)
    ? ATTEMPTED_ACTION_TITLES[key]
    : undefined;
  return `${label ?? humanizeToken(attempted)} refused`;
}

/** Whether the row is a refused action, staff or member (drives the warning tint). */
export function isRefusedAction(entry: ActivityEntry): boolean {
  return entry.action?.toLowerCase() === REFUSED_ACTION;
}

/** The line for a refused action whose stored message is provider text with no known code. */
export const REFUSED_CARD_MESSAGE = 'The card was refused.';

/**
 * The plain message the desk saw on a refused action (`details.message`), scrubbed at
 * the render boundary. `null` for other rows or an empty message.
 *
 * fix-plain-card-errors: dink-api stores the refusal's message as-is, so a passed-through
 * pay-api fault ("Square rejected the card step: CARD_DECLINED") lands here word for word.
 * Such text reads its plain decline line, a known vault sentence its own line, and any
 * other provider text {@link REFUSED_CARD_MESSAGE}. dink's own messages pass as before.
 */
export function refusedActionMessage(entry: ActivityEntry): string | null {
  if (!isRefusedAction(entry)) return null;
  const message = detailsText(entry, 'message');
  const vaultLine = knownVaultSentenceLine(message);
  if (vaultLine !== null) return vaultLine;
  if (isProviderMessage(message)) {
    return declineReasonLine(declineReasonFromMessage(message)) ?? REFUSED_CARD_MESSAGE;
  }
  return scrubBrand(message);
}

/**
 * The Change cell text for an admin check-in's payment choice (DNKADM-0428):
 * "Charge $49.99" (money from `amountMinor` + `currency` via the shared formatter, never
 * a hard-coded symbol), "Comp", "Unpaid at check-in". `null` for any other row or a
 * `details` shape we do not recognise — the cell then renders as before.
 */
export function checkInPaymentChangeLabel(entry: ActivityEntry): string | null {
  if (entry.action?.toLowerCase() !== 'reservation_check_in') return null;
  const choice = detailsText(entry, 'paymentChoice');
  if (choice === 'COMP') return 'Comp';
  if (choice === 'UNPAID_AT_CHECK_IN') return 'Unpaid at check-in';
  if (choice === 'CHARGE') {
    const minor = entry.details?.['amountMinor'];
    const currency = detailsText(entry, 'currency');
    if (typeof minor !== 'number' || !Number.isFinite(minor) || currency === null) return 'Charge';
    try {
      return `Charge ${formatMinorMoney(minor, currency)}`;
    } catch {
      return 'Charge';
    }
  }
  return null;
}

/** Human label for the session-mode qualifier (DNKAPI-0088). */
const SESSION_MODE_LABEL: Record<SessionMode, string> = {
  'front-desk': 'Front Desk',
  open: 'Open',
};

/**
 * The self-service qualifier (DNKADM-0394 U2) — appended to a MEMBER/GUEST actor so the
 * desk can tell a member booking themselves from a desk action at a glance. Lowercase on
 * purpose: it is a parenthetical about HOW the action arrived, not a proper noun, and it
 * sits beside the admin "(Front Desk)/(Open)" qualifier without competing with it.
 */
const SELF_SERVICE_QUALIFIER: Record<'member' | 'guest', string> = {
  member: 'member app',
  // The same words as the reservation drawer's "Booked by" route ("Guest checkout",
  // `bookedByRoute` in reservations/calendar/derive.ts), so the two never disagree
  // (DNKADM-0427).
  guest: 'guest checkout',
};

/**
 * What a MEMBER/GUEST actor reads as when the server could resolve NO name and no email —
 * the ROLE, in words. Never the principal id (DNKADM-0394 U2).
 */
const SELF_SERVICE_FALLBACK: Record<'member' | 'guest', string> = {
  member: 'Member app',
  guest: 'Guest checkout',
};

/**
 * The actor line AS THE TABLE RENDERS IT (DNKADM-0035, extended DNKADM-0394 U2): the
 * actor's display name plus the qualifier that says where the action came from.
 *  - `system` → "System" (no id/name/mode resolution);
 *  - a **member/guest** actor → "Sophia Craft (member app)" — self-service, so the
 *    qualifier is unconditional; with no `name`/`email` it degrades to the ROLE in words,
 *    "Member app" / "Guest checkout" (a guest reads "Sam Visitor (guest checkout)",
 *    DNKADM-0427, matching the drawer's "Booked by");
 *  - an **admin** actor with a `sessionMode` → "Name (Front Desk)" / "Name (Open)"
 *    (the qualifier rides on the actor's `name ?? email ?? id` label);
 *  - any other actor → `name ?? email ?? id` with **no** qualifier (the session-mode
 *    qualifier is admin-only).
 * Returns `null` when the actor carries no displayable identity at all.
 *
 * **A member/guest actor NEVER falls back to `id`** — unlike the admin/legacy branch below
 * it, which may (the parser guarantees an admin actor carries a login there, never a raw
 * auth sub). On a self-service row the principal IS an opaque contact id, and rendering it
 * is the bug this unit exists to end: the server returns `null` rather than an id it could
 * not resolve, so an id on screen could only have been composed here.
 */
export function actorLineLabel(entry: ActivityEntry): string | null {
  const { type, name, email, id, sessionMode } = entry.actor;
  if (type === 'system') return 'System';
  if (type === 'member' || type === 'guest') {
    const who = name ?? email;
    return who === null
      ? SELF_SERVICE_FALLBACK[type]
      : `${who} (${SELF_SERVICE_QUALIFIER[type]})`;
  }
  const label = name ?? email ?? id;
  if (label === null) return null;
  if (type === 'admin' && sessionMode !== null) {
    return `${label} (${SESSION_MODE_LABEL[sessionMode]})`;
  }
  return label;
}

/** The label for an entry with no location scope — "All locations" (universal). */
const ALL_LOCATIONS_LABEL = 'All locations';

/**
 * A resolved service-location id→name lookup (DNKADM-0035). The Activity surfaces
 * load this ONCE from the shipped `getServiceLocations()` (reuse — no second
 * fetch) and pass it into {@link locationLabel}. A `Map` keeps the helper pure and
 * synchronous; when the load **fails**, callers pass `null` and the helper
 * fail-softs to the raw id (the feed is more important than the label).
 */
export type ServiceLocationNames = ReadonlyMap<string, string>;

/**
 * The location qualifier AS THE TABLE RENDERS IT (DNKADM-0035):
 *  - no scope (`serviceLocationId === null`) → "All locations" (universal);
 *  - a scoped id resolved by `names` → the location's human name;
 *  - a scoped id NOT in `names`, OR `names === null` (the location list failed to
 *    load) → the **raw id** — FAIL-SOFT, so an unresolved/failed lookup degrades
 *    the label but never breaks the feed.
 */
export function locationLabel(
  serviceLocationId: string | null,
  names: ServiceLocationNames | null,
): string {
  if (serviceLocationId === null) return ALL_LOCATIONS_LABEL;
  return names?.get(serviceLocationId) ?? serviceLocationId;
}

/**
 * The signed change as the row renders it (DNKADM-0029 U1 `LedgerLine`):
 * `+4`, `-2`, `0`. `null` when the entry carries no change. Kept here so the
 * search text (below) matches the on-screen sign exactly.
 */
function signedChange(change: number | null): string | null {
  if (change === null) return null;
  return `${change > 0 ? '+' : ''}${change}`;
}

/**
 * Build the case-insensitive searchable string for an entry (DNKADM-0029 U2,
 * extended DNKADM-0035), from the SAME rendered fields the row shows — so the
 * client-side text filter matches what the user reads, not the raw wire keys.
 * Composed of: the human title, the humanized bucket, the signed change and
 * balance, the **actor line** (name + the "(Front Desk)/(Open)" qualifier), the
 * member's `target.name` AND `target.email`, the **location** label, the human
 * `note`, the kind, and the purchase amount/plan/tier (amount in dollars and in
 * raw cents, so both "170" and "17000" match).
 *
 * `names` resolves the location id→name (DNKADM-0035) so a search matches the
 * location NAME the table shows; pass `null`/omit when the location list is
 * unavailable and the label fail-softs to the raw id (still searchable).
 *
 * Lower-cased once here; callers tokenize the query and substring-match against
 * this. Absent fields contribute nothing (no stray separators leak in).
 */
export function activitySearchText(
  entry: ActivityEntry,
  names: ServiceLocationNames | null = null,
): string {
  const parts: Array<string | number | null> = [
    activityTitle(entry),
    humanizeBucket(entry.bucket),
    signedChange(entry.change),
    entry.balance,
    // The actor line carries the "(Front Desk)/(Open)" qualifier, so a search on
    // "front desk" matches what the table shows.
    actorLineLabel(entry),
    entry.target.name,
    entry.target.email,
    // The location label ("All locations" / a resolved name / the raw id fallback).
    locationLabel(entry.serviceLocationId, names),
    entry.note,
    refusedActionMessage(entry),
    checkInPaymentChangeLabel(entry),
    entry.kind,
    // Purchase detail — dollars (e.g. "170") and raw cents (e.g. "17000") both match.
    entry.amountCents !== null ? entry.amountCents / 100 : null,
    entry.amountCents,
    entry.plan,
    entry.tier,
    entry.term,
  ];
  return parts
    .filter((p): p is string | number => p !== null && p !== '')
    .map((p) => String(p))
    .join(' ')
    .toLowerCase();
}

/**
 * Whether an entry matches a free-text query (DNKADM-0029 U2, extended
 * DNKADM-0035): tokenize the query on whitespace and AND-match each token as a
 * case-insensitive substring of the entry's {@link activitySearchText}. The
 * optional `names` lookup lets the match cover the rendered location NAME; without
 * it the location matches by its fail-soft id. An all-whitespace/empty query
 * matches everything (the caller short-circuits, but this stays total).
 */
export function matchesActivityQuery(
  entry: ActivityEntry,
  query: string,
  names: ServiceLocationNames | null = null,
): boolean {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length === 0) return true;
  const haystack = activitySearchText(entry, names);
  return tokens.every((t) => haystack.includes(t));
}
